import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle2, Star, TrendingUp, Phone, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isNativePlatform } from '@/services/platformService';
import { NATIVE_PRICES, WEB_PRICES } from '@/services/revenueCatService';
import providersHero from '@/assets/providers-hero.jpg';

const ServiceProvidersPage = () => {
  const { t } = useTranslation();

  // Dynamic pricing based on platform
  const isNative = isNativePlatform();
  const providerPrice = isNative ? NATIVE_PRICES.provider.annual : WEB_PRICES.provider.annual;
  const setupFee = isNative ? 0 : WEB_PRICES.provider.setup;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="container mx-auto px-4 py-4 max-w-7xl">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-5 w-5" />
          <span>{t('common.backToHome')}</span>
        </Link>
      </div>

      {/* Hero Section with Image */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={providersHero} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>

        <div className="relative container mx-auto px-4 max-w-7xl py-16 md:py-24">
          <div className="max-w-2xl space-y-6">
            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight">
              <span className="text-white whitespace-pre-line">
                {t('landing.providers.teaserTitleHighlight')}
              </span>
            </h1>
            <p className="text-xl md:text-2xl font-semibold text-white/95 mt-2">
              {t('landing.providers.sectionTitle')}
            </p>
            <p className="text-lg text-white/85">
              {t('landing.providers.sectionSubtitle')}
            </p>

            {/* Stats Row */}
            <div className="flex flex-wrap gap-8 pt-2">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{t('landing.providers.stat1Value')}</div>
                <div className="text-xs text-white/70">{t('landing.providers.stat1Label')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{t('landing.providers.stat2Value')}</div>
                <div className="text-xs text-white/70">{t('landing.providers.stat2Label')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{t('landing.providers.stat3Value')}</div>
                <div className="text-xs text-white/70">{t('landing.providers.stat3Label')}</div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link to="/register-provider">
                <Button size="lg" className="bg-pet-purple hover:bg-pet-purple/90 text-white font-semibold px-8 py-6 text-base">
                  {t('landing.providers.ctaPrimary')} →
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 hover:text-white px-8 py-6 text-base">
                  {t('landing.providers.ctaSecondary')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Star, titleKey: 'landing.providers.benefit1Title', descKey: 'landing.providers.benefit1Description', color: 'text-yellow-500' },
              { icon: CheckCircle2, titleKey: 'landing.providers.benefit2Title', descKey: 'landing.providers.benefit2Description', color: 'text-green-500' },
              { icon: Phone, titleKey: 'landing.providers.benefit3Title', descKey: 'landing.providers.benefit3Description', color: 'text-blue-500' },
              { icon: TrendingUp, titleKey: 'landing.providers.benefit4Title', descKey: 'landing.providers.benefit4Description', color: 'text-purple-500' },
              { icon: BarChart3, titleKey: 'landing.providers.benefit5Title', descKey: 'landing.providers.benefit5Description', color: 'text-orange-500' },
            ].map((benefit, i) => (
              <div key={i} className="bg-card rounded-2xl p-6 shadow-sm border border-border hover:shadow-md transition-shadow">
                <benefit.icon className={`h-8 w-8 ${benefit.color} mb-4`} />
                <h3 className="text-lg font-semibold text-foreground mb-2">{t(benefit.titleKey)}</h3>
                <p className="text-sm text-muted-foreground">{t(benefit.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex flex-col sm:flex-row justify-center gap-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <span className="text-sm font-medium text-foreground">{t(`landing.providers.trustBadge${i}`)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Card */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-lg">
          <div className="bg-card rounded-2xl p-8 shadow-lg border border-border text-center">
            <h3 className="text-2xl font-bold text-foreground mb-2">💰 {t('landing.providers.pricingTitle')}</h3>
            <div className="space-y-1 mb-6">
              <div className="text-4xl font-bold text-pet-purple">
                US${providerPrice.toFixed(2)} {t('common.perYear')}
              </div>
              <div className="text-sm text-muted-foreground">{t('landing.providers.pricingPayment')}</div>
              {setupFee > 0 && (
                <div className="text-base text-foreground">
                  + US${setupFee.toFixed(2)} {t('common.oneTimeSetupFee')}
                </div>
              )}
            </div>
            <ul className="space-y-3 mb-8 text-left">
              {[1, 2, 3, 4].map(i => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-foreground">{t(`landing.providers.pricingFeature${i}`)}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <Link to="/register-provider" className="flex-1 min-w-0">
                <Button className="w-full bg-pet-purple hover:bg-pet-purple/90 text-white font-semibold py-6 text-base sm:text-lg">
                  {t('landing.providers.ctaPrimary')} →
                </Button>
              </Link>
              <Link to="/services" className="flex-1 min-w-0">
                <Button variant="outline" className="w-full border-2 border-pet-purple text-pet-purple hover:bg-pet-purple/10 py-6 text-base sm:text-lg">
                  {t('landing.providers.ctaSecondary')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-foreground mb-10 text-center">{t('landing.providers.howItWorksTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="text-center space-y-3">
                <div className="w-12 h-12 bg-pet-purple text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto">
                  {i}
                </div>
                <h4 className="font-semibold text-foreground text-lg">{t(`landing.providers.step${i}Title`)}</h4>
                <p className="text-sm text-muted-foreground">{t(`landing.providers.step${i}Description`)}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/register-provider">
              <Button size="lg" className="bg-pet-purple hover:bg-pet-purple/90 text-white text-lg px-8 py-6">
                {t('landing.providers.ctaFinal')} →
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceProvidersPage;
